import {
    DestroyRef,
    DOCUMENT,
    Injectable,
    NgZone,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { PresenceApiService } from '@dxp/core/api';
import { EMPTY, Observable, fromEvent, merge, timer } from 'rxjs';
import {
    auditTime,
    catchError,
    distinctUntilChanged,
    exhaustMap,
    map,
    startWith,
    switchMap,
    tap,
} from 'rxjs/operators';
import { environment } from '@dxp/core/environment';
import { LoggerService } from '../logger/logger.service';

@Injectable({ providedIn: 'root' })
export class SessionService {
    readonly #destroyRef = inject(DestroyRef);
    readonly #oidcSecurityService = inject(OidcSecurityService);
    readonly #presenceApiService = inject(PresenceApiService);
    readonly #logger = inject(LoggerService);
    readonly #ngZone = inject(NgZone);
    readonly #document = inject(DOCUMENT);

    readonly #userActivityEvents$: Observable<Event> = merge(
        fromEvent(this.#document, 'mousemove', { passive: true }),
        fromEvent(this.#document, 'keydown', { passive: true }),
        fromEvent(this.#document, 'wheel', { passive: true }),
        fromEvent(this.#document, 'touchstart', { passive: true }),
    );

    constructor() {
        this.#ngZone.runOutsideAngular(() => {
            this.#oidcSecurityService.isAuthenticated$
                .pipe(
                    map(r => r.isAuthenticated),
                    distinctUntilChanged(),
                    switchMap(isAuthenticated => {
                        const timeoutMinutes = environment.inActivityTimeOut;

                        if (!isAuthenticated || timeoutMinutes <= 0) {
                            return EMPTY;
                        }

                        return this.#createInactivityLogoutStream(
                            timeoutMinutes,
                        );
                    }),
                    takeUntilDestroyed(this.#destroyRef),
                )
                .subscribe();
        });
    }

    #createInactivityLogoutStream(timeoutMinutes: number): Observable<unknown> {
        const timeoutMs = this.#minutesToMs(timeoutMinutes);
        const activity$ = this.#userActivityEvents$.pipe(auditTime(250));

        return activity$.pipe(
            startWith(null),
            switchMap(() => timer(timeoutMs)),
            exhaustMap(() =>
                this.#presenceApiService.logout().pipe(
                    tap(() => {
                        this.#ngZone.run(() => {
                            this.#logger.warn(
                                'SessionService -> Inactivity timeout reached, logging out.',
                            );
                            localStorage.clear();
                            sessionStorage.clear();
                        });
                    }),
                    catchError(err => {
                        this.#ngZone.run(() => {
                            this.#logger.error(
                                'SessionService -> Logout failed after inactivity timeout.',
                                err,
                            );
                            localStorage.clear();
                            sessionStorage.clear();
                        });

                        return EMPTY;
                    }),
                ),
            ),
        );
    }

    #minutesToMs(minutes: number): number {
        if (!Number.isFinite(minutes) || minutes <= 0) {
            return 0;
        }

        return Math.floor(minutes * 60_000);
    }
}
